<template>
    <div class="bg-gray-100 min-h-screen">
        <!-- AI审计专属页面 -->
        <AiAuditDetail v-if="isAiAudit" />

        <!-- 审计数据模型专属页面 -->
        <AuditDataModelDetail v-else-if="isAuditDataModel" />

        <!-- 政府公示数据采集审计平台专属页面 -->
        <GovernmentInfoCollectionDetail v-else-if="isGovernmentInfoCollection" />

        <!-- 经责审计智能评价专属页面 -->
        <EconomicResponsibilityAuditDetail v-else-if="isEconomicResponsibilityAudit" />

        <!-- 审计数据集市专属页面 -->
        <AuditDataMarketDetail v-else-if="isAuditDataMarket" />

        <!-- 企业审计经营画像专属页面 -->
        <AuditBusinessProfileDetail v-else-if="isAuditBusinessProfile" />

        <!-- 审计作业系统专属页面 -->
        <AuditOperationDetail v-else-if="isAuditOperation" />

        <!-- 企业风险管理专属页面 -->
        <EnterpriseRiskManagementDetail v-else-if="isEnterpriseRiskManagement" />

        <!-- 穿透式监管专属页面 -->
        <PenetratingSupervisionDetail v-else-if="isPenetratingSupervision" />

        <!-- 风控合规数字化平台专属页面 -->
        <RiskComplianceDetail v-else-if="isRiskCompliance" />

        <!-- 智能数据管理与治理平台专属页面 -->
        <DataGovernanceDetail v-else-if="isDataGovernance" />

        <!-- 智慧审计与全面风险管理专属页面 -->
        <SmartAuditRiskDetail v-else-if="isSmartAuditRisk" />

        <!-- AI智能体平台专属页面 -->
        <AiAgentDetail v-else-if="isAiAgent" />

        <!-- 通用产品详情页面 -->
        <div v-else-if="productData" class="container mx-auto px-4 py-20">
            <!-- 面包屑导航 -->
            <div class="mb-8">
                <nav class="text-sm">
                    <router-link to="/" class="text-gray-500 hover:text-brand-blue">首页</router-link>
                    <span class="mx-2 text-gray-400">/</span>
                    <router-link to="/product" class="text-gray-500 hover:text-brand-blue">产品中心</router-link>
                    <span class="mx-2 text-gray-400">/</span>
                    <span class="text-brand-blue font-medium">{{ productData.name }}</span>
                </nav>
            </div>

            <!-- 产品头部 -->
            <div class="bg-white rounded-3xl shadow-xl p-12 mb-8">
                <div class="grid md:grid-cols-2 gap-12 items-center">
                    <!-- 左侧：产品信息 -->
                    <div>
                        <h1 class="text-4xl font-bold text-deep-indigo mb-4">{{ productData.name }}</h1>
                        <p class="text-xl text-gray-600 mb-6">{{ productData.slogan }}</p>
                        <div class="flex flex-wrap gap-3 mb-8">
                            <span v-for="tag in productData.tags" :key="tag" class="tag-large">{{ tag }}</span>
                        </div>
                        <p class="text-gray-700 leading-relaxed">{{ productData.description }}</p>
                    </div>
                    <!-- 右侧：产品图示 -->
                    <div class="flex items-center justify-center">
                        <div class="product-icon-large">
                            <i :class="productData.icon" class="text-8xl text-brand-blue"></i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 核心功能 -->
            <div class="mb-8">
                <h2 class="text-3xl font-bold text-deep-indigo mb-6 text-center">核心功能</h2>
                <div class="grid md:grid-cols-3 gap-6">
                    <div v-for="feature in productData.features" :key="feature.title" class="feature-card">
                        <div class="feature-icon">
                            <i :class="feature.icon" class="text-3xl text-brand-blue"></i>
                        </div>
                        <h3 class="text-xl font-bold text-deep-indigo mb-3">{{ feature.title }}</h3>
                        <p class="text-gray-600">{{ feature.description }}</p>
                    </div>
                </div>
            </div>

            <!-- 产品优势 -->
            <div class="bg-white rounded-3xl shadow-xl p-12 mb-8">
                <h2 class="text-3xl font-bold text-deep-indigo mb-8 text-center">产品优势</h2>
                <div class="grid md:grid-cols-2 gap-6">
                    <div v-for="(advantage, index) in productData.advantages" :key="index" class="advantage-item">
                        <div class="flex items-start">
                            <div class="advantage-number">{{ index + 1 }}</div>
                            <div class="flex-1">
                                <h4 class="text-lg font-bold text-deep-indigo mb-2">{{ advantage.title }}</h4>
                                <p class="text-gray-600">{{ advantage.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 应用场景 -->
            <div class="mb-8">
                <h2 class="text-3xl font-bold text-deep-indigo mb-6 text-center">应用场景</h2>
                <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div v-for="scenario in productData.scenarios" :key="scenario" class="scenario-card">
                        <i class="fas fa-check-circle text-2xl text-brand-blue mb-3"></i>
                        <p class="text-gray-700 font-medium">{{ scenario }}</p>
                    </div>
                </div>
            </div>

            <!-- CTA区域 -->
            <div class="bg-gradient-to-r from-brand-blue to-deep-indigo rounded-3xl shadow-xl p-12 text-center text-white">
                <h2 class="text-3xl font-bold mb-4">想了解更多关于 {{ productData.name }} 的信息？</h2>
                <p class="text-xl mb-8 opacity-90">我们的专家团队将为您提供专业的产品咨询和解决方案</p>
                <div class="flex gap-4 justify-center">
                    <router-link to="/consulting" class="btn-cta">咨询方案</router-link>
                    <button @click="goBack" class="btn-cta-outline">返回产品列表</button>
                </div>
            </div>
        </div>

        <!-- 加载状态 -->
        <div v-else class="container mx-auto px-4 py-20 text-center">
            <p class="text-gray-500 text-lg">加载中...点击菜单其他页</p>
        </div>
    </div>
</template>

<script>
import AiAuditDetail from './ProductDetail_AiAudit.vue';
import AuditDataModelDetail from './ProductDetail_AuditDataModel.vue';
import GovernmentInfoCollectionDetail from './ProductDetail_GovernmentInfoCollection.vue';
import EconomicResponsibilityAuditDetail from './ProductDetail_EconomicResponsibilityAudit.vue';
import AuditDataMarketDetail from './ProductDetail_AuditDataMarket.vue';
import AuditBusinessProfileDetail from './ProductDetail_AuditBusinessProfile.vue';
import AuditOperationDetail from './ProductDetail_AuditOperation.vue';
import EnterpriseRiskManagementDetail from './ProductDetail_EnterpriseRiskManagement.vue';
import PenetratingSupervisionDetail from './ProductDetail_PenetratingSupervision.vue';
import RiskComplianceDetail from './ProductDetail_RiskCompliance.vue';
import DataGovernanceDetail from './ProductDetail_DataGovernance.vue';
import SmartAuditRiskDetail from './ProductDetail_SmartAuditRisk.vue';
import AiAgentDetail from './ProductDetail_AiAgent.vue';

export default {
    name: 'ProductDetail',
    components: {
        AiAuditDetail,
        AuditDataModelDetail,
        GovernmentInfoCollectionDetail,
        EconomicResponsibilityAuditDetail,
        AuditDataMarketDetail,
        AuditBusinessProfileDetail,
        AuditOperationDetail,
        EnterpriseRiskManagementDetail,
        PenetratingSupervisionDetail,
        RiskComplianceDetail,
        DataGovernanceDetail,
        SmartAuditRiskDetail,
        AiAgentDetail
    },
    data() {
        return {
            productData: null,
            productsMap: {
                'audit-management': {
                    name: '审计管理系统',
                    slogan: '全流程审计管理，让审计工作更高效',
                    description: '实现从审计计划制定、资源调度、项目执行到档案归档的全流程管理，支持多项目并行，实现审计业务协同，提升审计工作效率和质量。',
                    tags: ['审计计划', '项目管理', '底稿管理', '档案归档'],
                    icon: 'fas fa-clipboard-list',
                    features: [
                        { title: '审计计划', icon: 'fas fa-calendar-alt', description: '年度审计计划编制、审批、下达，支持计划动态调整' },
                        { title: '项目管理', icon: 'fas fa-tasks', description: '项目立项、实施、报告、归档全生命周期管理' },
                        { title: '资源调度', icon: 'fas fa-users', description: '审计人员、预算、时间等资源的统一调度和分配' },
                        { title: '底稿管理', icon: 'fas fa-file-alt', description: '审计底稿在线编辑、审核、归档，支持模板化' },
                        { title: '档案管理', icon: 'fas fa-archive', description: '审计档案电子化管理，支持全文检索' },
                        { title: '协同工作', icon: 'fas fa-people-arrows', description: '多角色协同工作，实时沟通和任务流转' }
                    ],
                    advantages: [
                        { title: '全流程覆盖', description: '覆盖审计工作的各个环节，实现端到端管理' },
                        { title: '高度协同', description: '支持多项目、多人员并行工作，提升协作效率' },
                        { title: '规范化管理', description: '内置审计规范和模板，确保工作质量' },
                        { title: '数据积累', description: '审计数据持续积累，形成知识资产' }
                    ],
                    scenarios: ['年度审计计划管理', '专项审计项目管理', '审计底稿编制', '审计档案归档']
                },
                'audit-operation': {
                    name: '审计作业系统',
                    slogan: '移动化审计作业，随时随地高效取证',
                    description: '支持移动端和PC端一体化作业，提供离线取证、疑点实时上报、现场协同等功能，让审计人员在现场也能高效完成审计工作。',
                    tags: ['现场作业', '疑点管理', '底稿编制', '移动办公'],
                    icon: 'fas fa-mobile-alt',
                    features: [
                        { title: '移动取证', icon: 'fas fa-camera', description: '支持拍照、录音、录像等多种取证方式，离线也可使用' },
                        { title: '疑点管理', icon: 'fas fa-exclamation-triangle', description: '现场发现疑点可即时记录并上报，支持协同处理' },
                        { title: '底稿编制', icon: 'fas fa-edit', description: '移动端和PC端同步编制审计底稿，提升效率' },
                        { title: '现场协同', icon: 'fas fa-comments', description: '审计组成员实时沟通，任务协同完成' },
                        { title: '数据采集', icon: 'fas fa-download', description: '现场采集被审计单位数据，支持多种格式' },
                        { title: '进度跟踪', icon: 'fas fa-chart-line', description: '实时查看审计项目进度和任务完成情况' }
                    ],
                    advantages: [
                        { title: '移动化办公', description: 'PC端和移动端无缝衔接，随时随地开展工作' },
                        { title: '离线作业', description: '支持离线模式，网络不佳时也能正常工作' },
                        { title: '实时协同', description: '审计组成员实时沟通，提升协作效率' },
                        { title: '数据同步', description: '数据自动同步，避免数据丢失' }
                    ],
                    scenarios: ['现场审计作业', '移动办公取证', '疑点实时上报', '审计协同工作']
                },
                'audit-supervision': {
                    name: '审计监督评价系统',
                    slogan: '量化评价审计质量，构建监督闭环',
                    description: '对审计质量、整改成效进行量化评价，建立审计监督预警机制，形成问题跟踪和监督闭环，提升审计工作的监督效能。',
                    tags: ['监督预警', '绩效评价', '问题跟踪', '质量管控'],
                    icon: 'fas fa-chart-bar',
                    features: [
                        { title: '质量评价', icon: 'fas fa-star', description: '多维度评价审计项目质量，量化评分' },
                        { title: '监督预警', icon: 'fas fa-bell', description: '关键节点自动预警，防范审计风险' },
                        { title: '问题跟踪', icon: 'fas fa-search', description: '审计发现问题的整改跟踪，确保闭环' },
                        { title: '绩效评估', icon: 'fas fa-trophy', description: '审计人员和部门的绩效量化评估' },
                        { title: '统计分析', icon: 'fas fa-chart-pie', description: '审计数据统计分析，提供决策支持' },
                        { title: '报表生成', icon: 'fas fa-file-export', description: '自动生成各类监督评价报表' }
                    ],
                    advantages: [
                        { title: '量化管理', description: '用数据说话，让评价更客观、更公正' },
                        { title: '预警机制', description: '关键风险点实时预警，防患于未然' },
                        { title: '闭环管理', description: '问题发现、整改、验收形成完整闭环' },
                        { title: '持续改进', description: '基于评价结果持续优化审计工作' }
                    ],
                    scenarios: ['审计质量评价', '整改效果监督', '审计人员绩效评估', '审计数据统计分析']
                },
                'audit-data-market': {
                    name: '审计数据集市',
                    slogan: '整合多源数据，构建审计专用数据湖',
                    description: '整合财务、业务、外部数据，构建审计专用数据湖，提供数据采集、治理、服务能力，为审计分析提供高质量数据支撑。',
                    tags: ['数据采集', '数据治理', '数据服务', '数据质量'],
                    icon: 'fas fa-database',
                    features: [
                        { title: '数据采集', icon: 'fas fa-cloud-download-alt', description: '支持多种数据源的自动化采集和接入' },
                        { title: '数据治理', icon: 'fas fa-cogs', description: '数据清洗、标准化、质量管理' },
                        { title: '数据服务', icon: 'fas fa-server', description: '为各审计系统提供统一的数据服务接口' },
                        { title: '数据目录', icon: 'fas fa-folder-open', description: '数据资产目录管理，方便查找和使用' },
                        { title: '数据安全', icon: 'fas fa-shield-alt', description: '数据权限管理、脱敏、审计日志' },
                        { title: '数据分析', icon: 'fas fa-chart-area', description: '提供数据分析和查询工具' }
                    ],
                    advantages: [
                        { title: '统一数据源', description: '消除数据孤岛，实现数据统一管理' },
                        { title: '数据质量高', description: '通过治理确保数据的准确性和完整性' },
                        { title: '服务化', description: '数据以服务方式提供，方便各系统调用' },
                        { title: '安全可控', description: '完善的数据安全和权限管理机制' }
                    ],
                    scenarios: ['多源数据整合', '审计数据准备', '数据质量管理', '数据资产管理']
                },
                'audit-command-center': {
                    name: '审计指挥舱',
                    slogan: '领导驾驶舱，动态掌控审计全局',
                    description: '为审计领导提供实时监控、风险地图、指挥调度功能，动态呈现审计项目进展与风险态势，支持科学决策。',
                    tags: ['实时监控', '风险地图', '指挥调度', '决策支持'],
                    icon: 'fas fa-tv',
                    features: [
                        { title: '实时监控', icon: 'fas fa-eye', description: '审计项目进度、人员状态实时监控' },
                        { title: '风险地图', icon: 'fas fa-map-marked-alt', description: '可视化展示各业务领域的风险分布' },
                        { title: '指挥调度', icon: 'fas fa-sitemap', description: '审计资源的统一指挥和调度' },
                        { title: '数据大屏', icon: 'fas fa-desktop', description: '大屏展示关键指标和趋势' },
                        { title: '预警提醒', icon: 'fas fa-exclamation-circle', description: '关键风险和异常情况自动预警' },
                        { title: '决策分析', icon: 'fas fa-lightbulb', description: '基于数据的决策建议和分析' }
                    ],
                    advantages: [
                        { title: '全局视角', description: '一屏掌控所有审计项目和资源' },
                        { title: '实时动态', description: '数据实时更新，及时发现问题' },
                        { title: '可视化', description: '图表化展示，一目了然' },
                        { title: '智能预警', description: 'AI算法识别异常，提前预警' }
                    ],
                    scenarios: ['审计项目监控', '风险态势感知', '资源调度指挥', '领导决策支持']
                },
                'audit-business-profile': {
                    name: '审计经营画像',
                    slogan: '多维画像，精准刻画企业经营特征',
                    description: '多维度勾勒被审计单位经营特征，构建企业画像和风险画像，辅助审计人员精准识别风险、科学制定审计方案。',
                    tags: ['企业画像', '风险画像', '决策支持', '精准审计'],
                    icon: 'fas fa-user-circle',
                    features: [
                        { title: '企业画像', icon: 'fas fa-building', description: '从财务、业务、管理等多维度刻画企业特征' },
                        { title: '风险画像', icon: 'fas fa-exclamation-triangle', description: '基于历史数据和行业对标识别企业风险点' },
                        { title: '对标分析', icon: 'fas fa-balance-scale', description: '与行业标杆和历史数据对比分析' },
                        { title: '趋势预测', icon: 'fas fa-chart-line', description: '基于历史数据预测未来趋势' },
                        { title: '关系图谱', icon: 'fas fa-project-diagram', description: '展示企业关联方和交易关系' },
                        { title: '智能推荐', icon: 'fas fa-magic', description: '推荐审计重点和关注领域' }
                    ],
                    advantages: [
                        { title: '全方位画像', description: '从多个维度全面了解被审计单位' },
                        { title: '精准识别', description: '准确识别高风险领域和关键问题' },
                        { title: '智能分析', description: '利用AI技术进行智能分析和预测' },
                        { title: '决策支持', description: '为审计方案制定提供数据支持' }
                    ],
                    scenarios: ['审计对象分析', '风险识别评估', '审计方案制定', '审计重点确定']
                },
                'economic-responsibility-audit': {
                    name: '经责审计智能评价',
                    slogan: '智能评价体系，科学评估领导经济责任',
                    description: '内置经责审计评价指标体系，基于多维数据自动评价，支持任中审计和离任审计，自动生成经责审计报告。',
                    tags: ['经责评价', '指标模型', '任中审计', '离任审计'],
                    icon: 'fas fa-gavel',
                    features: [
                        { title: '指标体系', icon: 'fas fa-list-ol', description: '内置完整的经责审计评价指标体系' },
                        { title: '自动评价', icon: 'fas fa-calculator', description: '基于数据自动计算评价得分' },
                        { title: '任中审计', icon: 'fas fa-user-clock', description: '支持领导人员任职期间的动态监督' },
                        { title: '离任审计', icon: 'fas fa-sign-out-alt', description: '领导人员离任时的经济责任审计' },
                        { title: '对比分析', icon: 'fas fa-columns', description: '任期前后对比、同类单位对比分析' },
                        { title: '报告生成', icon: 'fas fa-file-pdf', description: '自动生成经责审计报告' }
                    ],
                    advantages: [
                        { title: '科学评价', description: '基于指标体系，评价更客观、更科学' },
                        { title: '高效便捷', description: '自动化评价，大幅提升审计效率' },
                        { title: '全面覆盖', description: '覆盖经济责任的各个方面' },
                        { title: '证据充分', description: '评价结论有数据支撑，证据充分' }
                    ],
                    scenarios: ['领导干部离任审计', '任期经济责任评价', '经责审计报告编制', '对比分析']
                },
                'government-info-collection': {
                    name: '政府权威信息采集',
                    slogan: '实时抓取监管动态，提供外部审计证据',
                    description: '实时抓取政府部门监管动态、处罚信息、政策法规等权威信息，为审计提供外部证据支持，拓展审计线索来源。',
                    tags: ['政策采集', '舆情监测', '数据抓取', '外部证据'],
                    icon: 'fas fa-globe',
                    features: [
                        { title: '政策采集', icon: 'fas fa-file-contract', description: '自动采集相关政策法规和监管要求' },
                        { title: '处罚信息', icon: 'fas fa-gavel', description: '抓取政府部门的行政处罚信息' },
                        { title: '舆情监测', icon: 'fas fa-newspaper', description: '监测与被审计单位相关的舆情信息' },
                        { title: '行业动态', icon: 'fas fa-industry', description: '采集行业监管和发展动态' },
                        { title: '智能匹配', icon: 'fas fa-link', description: '自动匹配与审计对象相关的信息' },
                        { title: '证据管理', icon: 'fas fa-folder', description: '外部信息作为审计证据进行管理' }
                    ],
                    advantages: [
                        { title: '权威来源', description: '信息来自政府官方渠道，权威可信' },
                        { title: '实时更新', description: '信息实时抓取，保证时效性' },
                        { title: '自动匹配', description: 'AI自动识别和匹配相关信息' },
                        { title: '证据支撑', description: '为审计结论提供外部证据支持' }
                    ],
                    scenarios: ['监管处罚信息采集', '政策法规跟踪', '舆情监测分析', '外部证据收集']
                },
                'audit-rectification': {
                    name: '审计整改闭环系统',
                    slogan: '问题整改全流转，确保问题见底清零',
                    description: '审计发现问题的整改任务线上流转，支持整改计划、进度跟踪、到期预警、验收销号，确保问题见底清零。',
                    tags: ['问题整改', '跟踪督办', '销号管理', '闭环管理'],
                    icon: 'fas fa-redo',
                    features: [
                        { title: '整改派单', icon: 'fas fa-file-signature', description: '审计问题自动派发整改任务' },
                        { title: '进度跟踪', icon: 'fas fa-tasks', description: '实时跟踪整改进度和完成情况' },
                        { title: '到期预警', icon: 'fas fa-alarm-clock', description: '整改任务到期自动预警提醒' },
                        { title: '整改验收', icon: 'fas fa-check-double', description: '整改完成后的验收和审核' },
                        { title: '销号管理', icon: 'fas fa-times-circle', description: '问题整改完成后的销号管理' },
                        { title: '统计分析', icon: 'fas fa-chart-bar', description: '整改情况统计分析和报表' }
                    ],
                    advantages: [
                        { title: '全流程管理', description: '从派单到销号的全流程线上管理' },
                        { title: '实时监控', description: '整改进度实时掌握，问题不遗漏' },
                        { title: '自动预警', description: '到期自动提醒，防止超期' },
                        { title: '闭环管理', description: '确保每个问题都得到有效整改' }
                    ],
                    scenarios: ['审计问题整改', '整改进度跟踪', '整改督办催办', '整改验收销号']
                },
                'audit-anomaly-analysis': {
                    name: '审计疑点分析系统',
                    slogan: '智能算法识别异常，精准推送审计线索',
                    description: '通过算法模型自动识别异常交易和可疑行为，利用大数据和AI技术挖掘审计疑点，精准推送审计线索。',
                    tags: ['疑点挖掘', '智能分析', '线索推荐', 'AI算法'],
                    icon: 'fas fa-search-dollar',
                    features: [
                        { title: '异常检测', icon: 'fas fa-search-plus', description: '基于规则和算法自动检测异常交易' },
                        { title: '模式识别', icon: 'fas fa-fingerprint', description: '识别可疑的交易模式和行为模式' },
                        { title: '关联分析', icon: 'fas fa-project-diagram', description: '分析交易、人员、机构之间的关联关系' },
                        { title: '线索推荐', icon: 'fas fa-bullseye', description: '基于分析结果推荐审计线索' },
                        { title: '可视化分析', icon: 'fas fa-chart-network', description: '图形化展示分析结果和关系网络' },
                        { title: '模型管理', icon: 'fas fa-brain', description: '审计模型的配置、训练、优化' }
                    ],
                    advantages: [
                        { title: '智能化', description: '利用AI和大数据技术，智能发现问题' },
                        { title: '精准定位', description: '准确定位高风险交易和可疑行为' },
                        { title: '效率提升', description: '大幅减少人工筛查工作量' },
                        { title: '持续优化', description: '模型不断学习和优化，越用越准' }
                    ],
                    scenarios: ['异常交易检测', '舞弊行为识别', '审计线索挖掘', '风险预警']
                },
                'ai-audit': {
                    name: 'AI 审计',
                    slogan: '大模型赋能审计，开启智能审计新时代',
                    description: '利用大语言模型自动阅读合同、凭证，智能分析财务报表，预测风险领域，为审计人员提供AI助手。',
                    tags: ['智能查账', 'NLP分析', '预测模型', '大模型'],
                    icon: 'fas fa-robot',
                    features: [
                        { title: '智能阅读', icon: 'fas fa-book-reader', description: '自动阅读和理解合同、凭证等文档' },
                        { title: 'NLP分析', icon: 'fas fa-language', description: '自然语言处理技术分析文本信息' },
                        { title: '风险预测', icon: 'fas fa-chart-line', description: '基于历史数据预测潜在风险领域' },
                        { title: '智能问答', icon: 'fas fa-comments', description: 'AI助手回答审计相关问题' },
                        { title: '自动分类', icon: 'fas fa-tags', description: '自动分类和标注审计资料' },
                        { title: '知识图谱', icon: 'fas fa-sitemap', description: '构建审计知识图谱，辅助决策' }
                    ],
                    advantages: [
                        { title: '前沿技术', description: '采用最新的大模型和AI技术' },
                        { title: '高度智能', description: '机器学习和深度学习，智能化程度高' },
                        { title: '效率革命', description: '大幅提升审计效率，减少重复劳动' },
                        { title: '持续进化', description: '模型持续学习优化，能力不断提升' }
                    ],
                    scenarios: ['合同智能审阅', '财务报表分析', '风险预测', 'AI审计助手']
                },
                'audit-data-model': {
                    name: '审计数据模型',
                    slogan: '300+审计模型库，开箱即用快速复用',
                    description: '预置300+审计模型和指标体系，覆盖各类审计场景，支持自定义和快速复用，加速审计分析工作。',
                    tags: ['审计模型库', '指标体系', '数据标准', '快速复用'],
                    icon: 'fas fa-cubes',
                    features: [
                        { title: '模型库', icon: 'fas fa-layer-group', description: '预置300+常用审计模型，开箱即用' },
                        { title: '指标体系', icon: 'fas fa-ruler-combined', description: '完整的审计指标体系和评价标准' },
                        { title: '自定义', icon: 'fas fa-sliders-h', description: '支持根据需求自定义审计模型' },
                        { title: '快速复用', icon: 'fas fa-copy', description: '模型一键复用，快速应用到新项目' },
                        { title: '版本管理', icon: 'fas fa-code-branch', description: '模型版本控制和演进管理' },
                        { title: '共享协作', icon: 'fas fa-share-alt', description: '审计模型在团队内共享和协作' }
                    ],
                    advantages: [
                        { title: '开箱即用', description: '预置模型丰富，可直接使用' },
                        { title: '标准化', description: '统一的数据模型和指标体系' },
                        { title: '可扩展', description: '支持自定义，满足个性化需求' },
                        { title: '知识沉淀', description: '审计经验和知识模型化沉淀' }
                    ],
                    scenarios: ['审计模型应用', '指标体系构建', '审计经验沉淀', '快速复用']
                }
            }
        };
    },
    computed: {
        productType() {
            return this.$route.params.productType;
        },
        isAiAudit() {
            return this.$route.params.productType === 'ai-audit';
        },
        isAuditDataModel() {
            return this.$route.params.productType === 'audit-data-model';
        },
        isGovernmentInfoCollection() {
            return this.$route.params.productType === 'government-info-collection';
        },
        isEconomicResponsibilityAudit() {
            return this.$route.params.productType === 'economic-responsibility-audit';
        },
        isAuditDataMarket() {
            return this.$route.params.productType === 'audit-data-market';
        },
        isAuditBusinessProfile() {
            return this.$route.params.productType === 'audit-business-profile';
        },
        isAuditOperation() {
            return this.$route.params.productType === 'audit-operation';
        },
        isEnterpriseRiskManagement() {
            return this.$route.params.productType === 'enterprise-risk-management';
        },
        isPenetratingSupervision() {
            return this.$route.params.productType === 'penetrating-supervision';
        },
        isRiskCompliance() {
            return this.$route.params.productType === 'risk-compliance-platform';
        },
        isDataGovernance() {
            return this.$route.params.productType === 'data-governance-platform';
        },
        isSmartAuditRisk() {
            return this.$route.params.productType === 'smart-audit-risk';
        },
        isAiAgent() {
            return this.$route.params.productType === 'ai-agent';
        }
    },
    mounted() {
        this.loadProductData();
    },
    watch: {
        '$route.params.productType': {
            handler() {
                this.loadProductData();
            }
        }
    },
    methods: {
        loadProductData() {
            const productType = this.$route.params.productType;
            this.productData = this.productsMap[productType] || null;
            
            if (!this.productData) {
                console.error('Product not found:', productType);
            }

            // 滚动到页面顶部
            window.scrollTo(0, 0);
        },
        goBack() {
            this.$router.push('/product');
        }
    }
};
</script>

<style scoped>
/* 标签样式 */
.tag-large {
    background: var(--porcelain-blue);
    color: var(--brand-blue);
    font-weight: 600;
    border-radius: 30px;
    padding: 8px 20px;
    font-size: 0.95rem;
    border: 1px solid rgba(31,63,143,0.2);
    display: inline-block;
}

/* 产品图标 */
.product-icon-large {
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, var(--porcelain-blue) 0%, #ffffff 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 20px 50px -20px rgba(31,63,143,0.3);
}

/* 功能卡片 */
.feature-card {
    background: var(--white);
    border-radius: 20px;
    padding: 30px;
    border: 1px solid var(--divider);
    box-shadow: 0 8px 20px -8px rgba(31,63,143,0.1);
    transition: all 0.3s;
}

.feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px -12px rgba(31,63,143,0.2);
}

.feature-icon {
    width: 60px;
    height: 60px;
    background: var(--porcelain-blue);
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

/* 优势项目 */
.advantage-item {
    padding: 20px;
    border-radius: 15px;
    background: var(--gray-bg);
    transition: all 0.3s;
}

.advantage-item:hover {
    background: var(--porcelain-blue);
}

.advantage-number {
    width: 40px;
    height: 40px;
    background: var(--brand-blue);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.2rem;
    margin-right: 15px;
    flex-shrink: 0;
}

/* 应用场景卡片 */
.scenario-card {
    background: var(--white);
    border-radius: 15px;
    padding: 25px;
    text-align: center;
    border: 1px solid var(--divider);
    transition: all 0.3s;
}

.scenario-card:hover {
    border-color: var(--brand-blue);
    box-shadow: 0 10px 25px -8px rgba(31,63,143,0.15);
}

/* CTA按钮 */
.btn-cta {
    background: white;
    color: var(--brand-blue);
    padding: 15px 40px;
    border-radius: 40px;
    font-weight: 700;
    font-size: 1.1rem;
    text-decoration: none;
    display: inline-block;
    transition: all 0.3s;
}

.btn-cta:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}

.btn-cta-outline {
    background: white;
    color: black;
    border: 2px solid white;
    padding: 15px 30px;
    border-radius: 40px;
    font-weight: 700;
    font-size: 1.1rem;
    transition: all 0.3s;
    cursor: pointer;
}

.btn-cta-outline:hover {
    background: white;
    color: var(--brand-blue);
}

/* 响应式 */
@media (max-width: 768px) {
    .product-icon-large {
        width: 150px;
        height: 150px;
    }
    
    .product-icon-large i {
        font-size: 4rem !important;
    }
}
</style>
